import {CommonModule} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators, ValidatorFn, AbstractControl, ValidationErrors} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatNativeDateModule} from '@angular/material/core';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatDividerModule} from '@angular/material/divider';
import {MatButtonModule} from '@angular/material/button';
import {Subject, takeUntil} from "rxjs";
import {FileService} from "../../../../../../shared/services/file.service";
import {LoadingService} from "../../../../../../shared/services/loading.service";
import {IncentiveService} from "../../../../../../shared/services/incentive.service";
import {IIncentive} from "../../../../../../utils/interfaces/incentive";

interface IncentiveForm {
  branchId: FormControl<string | null>;
  title: FormControl<string | null>;
  noticePageTile: FormControl<string | null>;
  noticePageDescription: FormControl<string | null>;
  noticePageImg: FormControl<string | null>;
  winnerPageTitle: FormControl<string | null>;
  winnerPageDescription: FormControl<string | null>;
  winnerPageImg: FormControl<string | null>;
  startDate: FormControl<string | null>;
  endDate: FormControl<string | null>;
  numberOfDraws: FormControl<number | null>;
  shifts: FormControl<boolean | null>;
}

type ImageType = 'notice' | 'winner';

@Component({
  selector: 'app-unit-incentive-add.dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatButtonModule,
  ],
  templateUrl: './unit-incentive-add.dialog.component.html',
  styleUrl: './unit-incentive-add.dialog.component.scss',
  providers: [FileService],
})
export class UnitIncentiveAddDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<UnitIncentiveAddDialogComponent>);
  readonly data = inject<{ unitId: string }>(MAT_DIALOG_DATA);

  private readonly fileService = inject(FileService);
  private readonly loadingService = inject(LoadingService);
  private readonly incentiveService = inject(IncentiveService);
  private readonly unsubscribe$ = new Subject();

  form!: FormGroup<IncentiveForm>;
  minDate: Date = new Date(); // Data mínima para o datepicker
    hasShifts = true;

  constructor() {
    if (this.data === null || this.data === undefined || this.data.unitId === undefined || this.data.unitId === null) {
      this.closeDialog(false);
      throw new Error('Id da unidade não encontrado.');
    }
  }

  // Validador para garantir que a data final seja maior ou igual à data inicial
  private endDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const form = control.parent as FormGroup;
      if (!form) return null;

      const startDate = form.get('startDate')?.value;
      const endDate = control.value;

      if (!startDate || !endDate) return null;

      const start = new Date(startDate);
      const end = new Date(endDate);

      return start > end ? { endDateInvalid: true } : null;
    };
  }

  // Validador para garantir que a data inicial seja maior ou igual à data atual
  private startDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const selectedDate = new Date(control.value);
      const today = new Date();

      // Remove o horário para comparar apenas as datas
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      return selectedDate < today ? { startDateInvalid: true } : null;
    };
  }

  ngOnInit(): void {
    // Set minDate to start of current day
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);

    this.form = new FormGroup<IncentiveForm>({
      branchId: new FormControl<string>(this.data.unitId || '', [Validators.required]),
      title: new FormControl<string | null>(null, [Validators.required]),
      noticePageTile: new FormControl<string | null>(null, [Validators.required]),
      noticePageDescription: new FormControl<string | null>(null, [Validators.required]),
      noticePageImg: new FormControl<string | null>(null),
      winnerPageDescription: new FormControl<string | null>(null, [Validators.required]),
      winnerPageTitle: new FormControl<string | null>(null, [Validators.required]),
      winnerPageImg: new FormControl<string | null>(null),
      startDate: new FormControl<string | null>(null, [Validators.required, this.startDateValidator()]),
      endDate: new FormControl<string | null>(null, [Validators.required, this.endDateValidator()]),
      numberOfDraws: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
      shifts: new FormControl<boolean>(false, [Validators.required]),
    });

      // Buscar se há turnos cadastrados para a unidade
      // Supondo que a unidade foi passada via data.unitId
      // Aqui, você pode usar um serviço para buscar os turnos, mas para simplificação, vamos supor que já está disponível
      // Exemplo: this.hasShifts = unidade.availableShifts && unidade.availableShifts.length > 0;
      // Se precisar buscar, use o UnitsService.getUnitById(this.data.unitId)
      // Para este patch, deixo como true por padrão, mas pode ser ajustado conforme a lógica real

    // Adiciona um listener para atualizar a validação da data final quando a inicial mudar
    this.form.get('startDate')?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.form.get('endDate')?.updateValueAndValidity();
      });
  }

  cancel(): void {
    this.closeDialog(false);
  }

  resizeImage(event: Event, imageType: ImageType): void {
    const target = event.target as HTMLInputElement;
    const file = (target.files as FileList)[0];

    if (!file) return;

    this.loadingService.showLoading();

    this.fileService
      .resizeImage(file)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((newFile) => {
        this.uploadImage(newFile, imageType);
      });
  }

  uploadImage(file: File, imageType: ImageType): void {
    this.fileService
      .uploadImage(file)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (imageType === 'notice') {
          this.form.controls.noticePageImg.setValue(res.data);
        } else {
          this.form.controls.winnerPageImg.setValue(res.data);
        }
        this.loadingService.closeLoading();
      });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loadingService.showLoading();

    const incentiveData: IIncentive = {
      branchId: this.form.value.branchId as string,
      title: this.form.value.title as string,
      noticePageTile: this.form.value.noticePageTile as string,
      noticePageDescription: this.form.value.noticePageDescription as string,
      noticePageImg: this.form.value.noticePageImg || '',
      winnerPageTitle: this.form.value.winnerPageTitle as string,
      winnerPageDescription: this.form.value.winnerPageDescription as string,
      winnerPageImg: this.form.value.winnerPageImg || '',
      startDate: this.form.value.startDate as string,
      endDate: this.form.value.endDate as string,
      numberOfDraws: Number(this.form.value.numberOfDraws),
      shifts: Boolean(this.form.value.shifts)
    };

    this.incentiveService.post(incentiveData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.loadingService.closeLoading();
          if (response.success) {
            this.closeDialog(true);
          }
        }
      });
  }


  private closeDialog(state: boolean): void {
    this.dialogRef.close(state);
  }
}
